import {FormControl, InputLabel, MenuItem, OutlinedInput, Select} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';
import IconButton from '@mui/material/IconButton';
import {isEmpty} from 'lodash';
import ClearIcon from '@mui/icons-material/Clear';
import clsx from 'clsx';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';

const searchFiledStyle = makeStyles(theme => ({
    noBorder: {
        '& fieldset': {
            border: ({noBorder}) => noBorder ? 'none' : '',
        },
    },
    colorOutline: {
        '& fieldset': {
            borderColor: ({borderColor, borderColorIsArray}) =>
                borderColorIsArray ? (borderColor.length > 0 ? (borderColor[0] !== '' ? borderColor[0] + '!important' : 'inherit') : 'inherit') : (borderColor !== '' ? borderColor + '!important' : 'inherit'),
        },
        '&:hover fieldset': {
            borderColor: ({borderColor, borderColorIsArray}) =>
                borderColorIsArray ? (borderColor.length > 1 ? (borderColor[1] !== '' ? borderColor[1] + '!important' : 'inherit') : 'inherit') : (borderColor !== '' ? borderColor + '!important' : 'inherit'),
        },
    },
    background: {
        backgroundColor: theme.palette.background.field,
        borderRadius: ({borderRadius}) => borderRadius,
        '&.new-background': {
            backgroundColor: theme.palette.inPatient.inputBackground,
        },
    },
    item: {
        background: 'transparent',
    },
    font: {
        fontFamily: 'Inter',
        fontWeight: 500,
        fontSize: 12,
        '&.bigger': {
            fontSize: 14,
        },
    },
    menuPaper: {
        maxHeight: 250,
    },
    input: {
        '&.placeholder': {
            color: theme.palette.mode === 'light' ? '#acaeaf' : '#878787',
        },
    },
    inPatientBackground: {
        backgroundColor: `${theme.palette.inPatient.inputBackground} !important`,
    },
}));

const AgyleSelectField = (props) => {
    const classes = searchFiledStyle({
        noBorder: props.noBorder,
        borderColor: props.borderColor,
        borderColorIsArray: Array.isArray(props.borderColor),
        borderRadius: props.borderRadius,
    });
    const selectInput = useRef(null);
    const clearValue = props.multiple ? [] : '';
    const [open, setOpen] = useState(false);

    const handleChangeValue = (event) => {
        const {value} = event.target;
        props.onChange(value, event);
    };

    const handleClearClick = () => {
        if (!props.disabled)
            props.onChange(props.defaultClearValue || clearValue);
    };

    const handleToggleMenu = () => {
        if (!props.disabled)
            setOpen(prev => !prev);
    };

    const handleOpenState = (state) => {
        setOpen(state);
    };

    useEffect(() => {
        if (props.onOpen) props.onOpen(open);
        if (!open) {
            selectInput.current.blur();
            selectInput.current.classList.remove('Mui-focused');
        }
    }, [open]);

    const label = props.label || (props.placeholder && isEmpty(props.value) ? props.placeholder : '');

    return (
        <FormControl fullWidth={props.fullWidth} size={props.size}>
            <InputLabel
                id="agyle-select-label"
                size={props.size}
                // style={{color: theme.palette.mode === "light" ? "#000" : "#fff"}}
                shrink={(props.shrink || !isEmpty(props.value))}
                className={clsx(classes.input, props.placeholder && isEmpty(props.value) && 'placeholder', props.labelClasses)}
            >
                {label}
            </InputLabel>

            <Select
                open={open}
                error={props.error}
                multiple={props.multiple}
                onClose={handleOpenState.bind(this, false)}
                onOpen={handleOpenState.bind(this, true)}
                SelectDisplayProps={{style: {padding: props.padding}}}
                style={{
                    width: 'auto',
                    paddingLeft: '5px',
                    minHeight: '40px',
                    ...props.style,
                }}
                className={clsx(
                    !props.withBorder && classes.noBorder,
                    props.withBackground && classes.background,
                    props.withNewBackground && classes.inPatientBackground,
                    props.borderColor && classes.colorOutline,
                    props.selectClasses,
                    props.className,
                )}
                size={props.size}
                name={props.name}
                MenuProps={{classes: {paper: clsx('scroll-normal-white', classes.menuPaper, props.menuClasses)}}}
                value={props.value}
                renderValue={props.multiple ? (selected) => {
                    if (props.multiple) {
                        if (props.multipleInputRender) return props.multipleInputRender(selected);
                        else return <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                            {selected?.map((value) => (
                                <Chip key={value} label={value.description}/>
                            ))}
                        </Box>;
                    }
                } : props.viewRender ? (selected) => {
                    return props.viewRender(selected);
                } : null}
                onChange={handleChangeValue}
                input={<OutlinedInput
                    label={(props.multiple && isEmpty(props.value)) ? '' : label}
                    sx={{
                        fontFamily: 'Inter',
                        fontWeight: 500,
                        fontSize: 14,
                    }}
                    ref={selectInput}
                />}
                sx={{
                    '& .MuiSelect-iconOutlined': {display: 'none'},
                }}
                endAdornment={
                    <>
                        {!isEmpty(props.value) && props.clearButtonVisible &&
                            <IconButton
                                disableRipple={true}
                                onClick={handleClearClick}
                                size={'small'}
                                sx={{m: 0, p: 0}}
                                disabled={props.disabled}
                                style={{color: props.disabled ? 'grey' : (props.style?.color || '')}}
                            >
                                <ClearIcon
                                    fontSize="small"
                                />
                            </IconButton>
                        }
                        <IconButton
                            disableRipple={true}
                            onClick={handleToggleMenu}
                            size={'small'}
                            sx={{m: 0, p: 0}}
                            disabled={props.disabled}
                            style={{color: props.disabled ? 'grey' : (props.style?.color || '')}}
                        >
                            {open ? <ArrowDropUpIcon fontSize="small"/> : <ArrowDropDownIcon fontSize="small"/>}
                        </IconButton>
                    </>
                }
                disabled={props.disabled}
            >
                {props.options?.map(opt => {
                        if (props.customObject)
                            return props.customObject(opt);
                        else
                            return (
                                <MenuItem
                                    key={opt.id}
                                    value={opt.id}
                                    className={clsx(classes.item, classes.font, 'bigger')}
                                >
                                    {opt.name}
                                </MenuItem>
                            );
                    },
                )}
            </Select>
        </FormControl>
    );

};

export default AgyleSelectField;

AgyleSelectField.propTypes = {
    label: PropTypes.string,
    placeholder: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.array.isRequired,
    customObject: PropTypes.func,
    displayEmpty: PropTypes.bool,
    disabled: PropTypes.bool,
    defaultClearValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    withBorder: PropTypes.bool,
    borderColor: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    withBackground: PropTypes.bool,
    withNewBackground: PropTypes.bool,
    shrink: PropTypes.bool,
    style: PropTypes.object,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    clearButtonVisible: PropTypes.bool,
    padding: PropTypes.string,
    multiple: PropTypes.bool,
    withLabel: PropTypes.bool,
    viewRender: PropTypes.func,
    fullWidth: PropTypes.bool,
    selectClasses: PropTypes.any,
    labelClasses: PropTypes.any,
    name: PropTypes.string,
    error: PropTypes.bool,
    className: PropTypes.any,
    borderRadius: PropTypes.number
};

AgyleSelectField.defaultProps = {
    placeholder: '',
    label: '',
    error: false,
    displayEmpty: false,
    disabled: false,
    withBorder: false,
    withBackground: false,
    withNewBackground: false,
    shrink: false,
    size: 'small',
    clearButtonVisible: true,
    padding: '5px',
    multiple: false,
    withLabel: true,
    noBorder: true,
    fullWidth: true,
    borderColor: '',
    borderRadius: 6
};